<template>
  <div ref="paneRoot" class="gc-json-split__pane gc-json-split__pane--preview">
    <div class="gc-json-split__header">
      <div class="gc-json-split__header-text">
        <span class="gc-json-split__title">{{ previewTitle }}</span>
        <span class="gc-json-split__subtitle">{{ previewSubtitle }}</span>
      </div>
      <v-progress-circular
        v-if="loading"
        indeterminate
        size="20"
        width="2"
        color="#f5c347"
      />
    </div>
    <div class="gc-json-split__preview-body">
      <p v-if="!active" class="gc-json-split__empty">Use Compare with English to load</p>
      <p v-else-if="error" class="gc-json-split__error">{{ error }}</p>
      <p v-else-if="!hasContent" class="gc-json-split__empty">No content to translate</p>
      <p v-else-if="!hasPreview && !loading" class="gc-json-split__empty">Waiting for translation…</p>
      <vue-json-editor
        v-else
        ref="jsonEditor"
        :value="englishPreview"
        mode="view"
        :show-btns="false"
        :expandedOnStart="true"
        :modes="['view']"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import vueJsonEditor from 'vue-json-editor';
import { collectStringLeaves, applyTranslations } from '@/plugins/translation-json';
import { translateStrings } from '@/plugins/translate';
import { formatTranslationDetail } from '@/plugins/translation-progress';
import { getJsonEditorScrollElement } from '@/plugins/sync-json-editor-scroll';
import {
  clearJsonEditorCompareHighlights,
  scheduleJsonEditorCompareHighlights
} from '@/plugins/json-editor-compare-highlight';

export default {
  name: 'TranslationPreviewPane',

  components: {
    vueJsonEditor
  },

  props: {
    content: {
      type: [Object, Array, String],
      default: null
    },
    sourceLocale: {
      type: String,
      required: true
    },
    axios: {
      type: Function,
      required: true
    },
    trigger: {
      type: Number,
      default: 0
    },
    active: {
      type: Boolean,
      default: false
    },
    additionalPrompt: {
      type: String,
      default: ''
    },
    uiInfoConfig: {
      type: String,
      default: ''
    },
    translationModel: {
      type: String,
      default: ''
    },
    translationModelLabel: {
      type: String,
      default: ''
    },
    compareDiff: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      englishPreview: null,
      loading: false,
      error: '',
      requestId: 0
    };
  },

  computed: {
    ...mapState(['bucket']),
    hasContent() {
      return this.content !== null && this.content !== undefined && this.content !== '';
    },
    hasPreview() {
      return this.englishPreview !== null && this.englishPreview !== undefined;
    },
    previewTitle() {
      return 'English (machine)';
    },
    previewSubtitle() {
      return 'Google Translate · not saved';
    }
  },

  watch: {
    trigger() {
      if (this.trigger > 0 && this.active) {
        this.refreshPreview();
      }
    },
    compareDiff: {
      deep: true,
      handler() {
        this.refreshCompareHighlights();
      }
    },
    englishPreview() {
      this.refreshCompareHighlights();
    }
  },

  beforeDestroy() {
    this.requestId += 1;
    clearJsonEditorCompareHighlights(this.$refs.jsonEditor);
  },

  methods: {
    getScrollElement() {
      return (
        getJsonEditorScrollElement(this.$refs.jsonEditor) ||
        getJsonEditorScrollElement(this.$refs.paneRoot)
      );
    },

    refreshCompareHighlights() {
      if (!this.compareDiff || !this.$refs.jsonEditor) return;
      this.$nextTick(() => {
        scheduleJsonEditorCompareHighlights(this.$refs.jsonEditor, this.compareDiff, 'machine');
      });
    },

    async refreshPreview() {
      const requestId = ++this.requestId;

      if (!this.hasContent || typeof this.content === 'string') {
        this.englishPreview = {};
        this.error = '';
        this.loading = false;
        this.$emit('translating', { active: false });
        this.$emit('preview-ready', this.englishPreview);
        this.$emit('ready');
        return;
      }

      const entries = collectStringLeaves(this.content);
      if (!entries.length) {
        this.englishPreview = this.cloneStructure(this.content);
        this.error = '';
        this.loading = false;
        this.$emit('translating', { active: false });
        this.$emit('preview-ready', this.englishPreview);
        this.$emit('ready');
        return;
      }

      this.loading = true;
      this.error = '';
      this.$emit('translating', {
        active: true,
        detail: formatTranslationDetail({
          stringCount: entries.length,
          localeLabel: `${this.sourceLocale} → EN`,
          extra: 'Google Translate compare'
        })
      });

      try {
        const texts = entries.map(e => e.value);
        const translations = await translateStrings(this.axios, texts, {
          source: this.sourceLocale,
          target: 'en',
          mode: 'compare',
          bucket: this.bucket
        });

        if (requestId !== this.requestId) return;

        this.englishPreview = applyTranslations(this.content, entries, translations);
        this.error = '';
        this.loading = false;
        this.$emit('translating', { active: false });
        this.$emit('preview-ready', this.englishPreview);
        this.$emit('completed');
        this.$emit('ready');
        this.refreshCompareHighlights();
      } catch (e) {
        if (requestId !== this.requestId) return;
        this.error =
          e.response?.data?.message || e.message || 'Could not load English preview';
        this.loading = false;
        this.$emit('translating', { active: false });
        this.$emit('error', this.error);
        this.$emit('ready');
      }
    },

    cloneStructure(obj) {
      if (obj === null || typeof obj !== 'object') return obj;
      if (Array.isArray(obj)) return obj.map(item => this.cloneStructure(item));
      const copy = {};
      Object.keys(obj).forEach(key => {
        copy[key] = this.cloneStructure(obj[key]);
      });
      return copy;
    }
  }
};
</script>
